import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, Link } from "react-router-dom";
import { forgotPasswordSchema } from "../../schemas/forgotPasswordSchema";
import { useForgotPassword } from "../../custom-hooks/queries";
import { toast } from "../../components/common/Toast";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { mutate: forgotPassword, isPending } = useForgotPassword();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(forgotPasswordSchema),
  });

  const onSubmit = (data) => {
    forgotPassword(
      { email: data.email },
      {
        onSuccess: () => {
          toast.success("Code sent successfully! Check your email.");
          navigate("/otp", { state: { email: data.email, flow: "forgot-password" } });
        },
        onError: (error) => {
          toast.error(
            error.response?.data?.message ||
              "Failed to send code. Please try again."
          );
        },
      }
    );
  };

  return (
    <div className="flex items-center justify-center px-4 py-6 sm:py-8">
      <div className="w-full max-w-[500px] border border-gray-200 shadow-sm rounded-2xl p-6 sm:p-10 bg-white">
        {/* Title */}
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-2">
          Forgot Password
        </h1>
        <p className="text-sm sm:text-base text-gray-600 text-center mb-4">
          Enter your email address and we'll send you a code to reset your
          password.
        </p>
        {/* Decorative Rectangle */}
        <div className="flex justify-center mb-6 sm:mb-8">
          <div className="w-[165px] h-[5px] bg-[#16BBD8] rounded"></div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Email */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-800 mb-1"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              {...register("email")}
              className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1FADC6] focus:border-transparent transition-colors placeholder-gray-600 ${
                errors.email ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter your email address"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">
                {errors.email.message}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isPending}
            className="w-full bg-[#00C2C7] hover:bg-[#1FADC6] text-white font-medium py-3 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#1FADC6] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Sending..." : "Send Code"}
          </button>

          {/* Back to Sign In Link */}
          <p className="text-center text-sm text-gray-600">
            Remember your password?{" "}
            <Link
              to="/signin"
              className="font-medium text-[#00C2C7] hover:text-[#16BBD8]"
            >
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;
